import { Component, Element, Event, EventEmitter, forceUpdate, h, Host, Listen, Prop, State, Watch } from "@stencil/core";
import { i18nProvider } from "@trumpf/ux.i18n";

@Component({
    tag: "ux-onboarding-guide",
    styleUrl: "./onboarding-guide.scss",
    shadow: true
})
export class OnboardingGuide {
    @Element() el: HTMLUxOnboardingGuideElement;

    /**
     * Current step index
     */
    @Prop() readonly current: number = 0;

    /** Defines the content height behavior of the component. Type string sets the breakpoint value at which the component should no longer use auto height */
    @Prop() readonly autoHeight: boolean | string = false;
    /** Sets the opened state of the modal  */
    @Prop() readonly opened: boolean = false;

    @State() internalCurrent = 0;
    @State() internalOpened: boolean = this.opened;

    /**
     * Reference to the default slot element inside .content-container
     */
    private slotEl: HTMLSlotElement;

    @Watch("current")
    watchCurrent(value: number) {
        this.internalCurrent = value;
    }

    @Watch("opened")
    watchOpened(newValue: boolean) {
        this.internalOpened = newValue;
    }
    /**
     * Event for change of step index
     */
    @Event({
        eventName: "uxOnboardingGuideChange",
        composed: false,
        cancelable: false,
        bubbles: true
    })
    onboardingGuideChange: EventEmitter<{ closed: boolean; stepIndex: number }>;

    /**
     * Array of slotted elements (steps)
     */
    @State() steps: Element[] = [];

    @Listen("resize", { target: "window" })
    handleResize() {
        if (this.slotEl) {
            this.setContentHeight();
        }
    }

    private i18nSubscriberId!: string;

    connectedCallback() {
        this.i18nSubscriberId = i18nProvider.subscribe(() => forceUpdate(this));
    }

    disconnectedCallback() {
        i18nProvider.unsubscribe(this.i18nSubscriberId);
    }

    componentWillLoad() {
        this.watchCurrent(this.current);
    }

    componentDidRender() {
        if (this.slotEl) {
            const children = this.slotEl.assignedElements({ flatten: true });

            if (children.length !== this.steps.length) {
                this.steps = children;
            }
            this.setContentHeight();
        }
    }

    private setContentHeight = () => {
        if (this.slotEl && this.steps.length > 0) {
            let minHeight = 0;

            this.steps.forEach((child, index) => {
                (child as HTMLElement).style.display = "block";
                (child as HTMLElement).style.visibility = "hidden";
                minHeight = Math.max(minHeight, (child as HTMLElement).offsetHeight);
                this.slotEl.style.minHeight = this.autoHeight === false ? minHeight + "px" : "0px";
                if (typeof this.autoHeight === "string" && this.autoHeight.trim() !== "" && !isNaN(Number(this.autoHeight))) {
                    this.slotEl.style.minHeight = window.innerWidth < Number(this.autoHeight) ? "0px" : minHeight + "px";
                }
                (child as HTMLElement).style.visibility = "visible";
                (child as HTMLElement).style.display = index === this.internalCurrent ? "block" : "none";
            });
        }
    };

    private onModalClose = () => {
        this.internalOpened = false;
        this.onboardingGuideChange.emit({ closed: true, stepIndex: this.internalCurrent });
    };

    private goToNextStep = () => {
        if (this.internalCurrent < this.steps.length - 1) {
            this.internalCurrent++;
            this.onboardingGuideChange.emit({ closed: false, stepIndex: this.internalCurrent });
        }
    };

    private goToPreviousStep = () => {
        if (this.internalCurrent > 0) {
            this.internalCurrent--;
            this.onboardingGuideChange.emit({ closed: false, stepIndex: this.internalCurrent });
        }
    };

    render() {
        const { t } = i18nProvider;
        return (
            <Host>
                <ux-modal opened={this.internalOpened} name="ux-onboarding-guide-modal" onUxModalClose={this.onModalClose}>
                    <ux-modal-content>
                        <div class="container">
                            {this.internalCurrent === null ? (
                                <slot name="spinner"></slot>
                            ) : (
                                <div>
                                    <ux-progress-bar current={this.internalCurrent + 1} steps={this.steps.length}>
                                        <slot name="progress-bar-button" slot="button" />
                                    </ux-progress-bar>
                                    <div class="content-container">
                                        <slot ref={(el) => (this.slotEl = el as HTMLSlotElement)}></slot>
                                    </div>
                                    <slot name={`navigation-${this.internalCurrent}`}>
                                        <div class="navigation">
                                            <ux-button
                                                class="prev-button"
                                                aria-label={t("general.prevButton")}
                                                onClick={this.goToPreviousStep}
                                            >
                                                <slot name="button-left" />
                                            </ux-button>
                                            <ux-button
                                                class="next-button"
                                                aria-label={t("general.nextButton")}
                                                onClick={this.goToNextStep}
                                            >
                                                <slot name="button-right" />
                                            </ux-button>
                                        </div>
                                    </slot>
                                </div>
                            )}
                        </div>
                    </ux-modal-content>
                </ux-modal>
            </Host>
        );
    }
}
